import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string) ?? "http://localhost:8001/api";

export interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string>;
  raw?: unknown;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ─── Lazy import to avoid circular deps ──────────────────────────────────────
let getAuthState:
  | (() => {
      token: string | null;
      refreshToken: string | null;
      setToken: (t: string) => void;
      logout: () => void;
    })
  | null = null;

export function setAuthStateGetter(fn: typeof getAuthState) {
  getAuthState = fn;
}

// ─── Request interceptor: attach Bearer token ─────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthState?.()?.token;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

// ─── Response interceptor: handle 401 / auto refresh ─────────────────────────
let isRefreshing = false;
let queue: Array<(t: string) => void> = [];

function processQueue(token: string) {
  queue.forEach((cb) => cb(token));
  queue = [];
}

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status;

    if (status === 401 && !original._retry && getAuthState) {
      const auth = getAuthState();
      if (!auth.refreshToken) {
        auth.logout();
        return Promise.reject(normalizeError(error));
      }

      if (isRefreshing) {
        return new Promise<string>((resolve) => queue.push(resolve)).then(
          (token) => {
            original.headers!.Authorization = `Bearer ${token}`;
            return apiClient(original);
          },
        );
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { useAuthStore: _store } = await import("@/store");
        const userId = _store.getState().user?._id;
        const { data } = await axios.post(`${BASE_URL}/auth/refresh-token`, {
          refreshToken: auth.refreshToken,
          userId,
        });
        const newToken: string = data.data?.token ?? data.token;
        auth.setToken(newToken);
        processQueue(newToken);
        original.headers!.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      } catch {
        auth.logout();
        return Promise.reject(normalizeError(error));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

function normalizeError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const data = error.response?.data as Record<string, unknown> | undefined;
  return {
    status,
    message: (data?.message as string) ?? error.message ?? "Unexpected error",
    errors: (data?.errors as Record<string, string>) ?? undefined,
    raw: data,
  };
}
